'use client';

import {
  BLOBS_STORE,
  mutateUploadRow,
  openUploadDatabase,
  readOpfsBlob,
  removeOpfsBlob,
  runUploadRequest,
  UPLOADS_STORE,
  type UploadBlobStorage,
  writeOpfsBlob,
} from './persisted-upload-backends';
import {
  mergePersistedUpload,
  persistedUploadOf,
  type NewPersistedUpload,
  type PersistedUpload,
  type PersistedUploadPatch,
} from './persisted-upload-record';

export {
  mergePersistedUpload,
  type NewPersistedUpload,
  type PersistedMultipartState,
  type PersistedUpload,
  type PersistedUploadIdentity,
  type PersistedUploadPatch,
} from './persisted-upload-record';

/**
 * Files handed to a background upload, kept in this browser so a reload can
 * resume them (#4844). Metadata lives in IndexedDB; the bytes go to OPFS,
 * else to an IndexedDB Blob, else nowhere (`blobStore: 'none'`: the UI asks
 * for the file again). Nothing here throws: private mode, SSR and a full
 * quota all degrade to "not persisted". A multipart session is kept by its
 * id only: its bearer token never reaches browser storage.
 */

export type PersistedUploadStore = {
  persistUpload: (
    record: NewPersistedUpload,
    file: Blob
  ) => Promise<PersistedUpload>;
  updateUpload: (
    uploadId: string,
    patch: PersistedUploadPatch
  ) => Promise<PersistedUpload | null>;
  listUploads: () => Promise<PersistedUpload[]>;
  readUploadFile: (record: PersistedUpload) => Promise<File | null>;
  deleteUpload: (uploadId: string) => Promise<void>;
  /** Whether this browser can keep a file's metadata and bytes right now. */
  probeDurableStorage: () => Promise<boolean>;
};

/** The row and file name the durability probe writes and deletes. */
const PROBE_ID = 'imai-durability-probe';

/** A row written before session ids replaced control URLs (#4844). */
function carriesControlUrl(row: unknown): boolean {
  if (typeof row !== 'object' || row === null) return false;
  const multipart = (row as { multipart?: unknown }).multipart;
  return (
    'uploadUrl' in row ||
    (typeof multipart === 'object' &&
      multipart !== null &&
      'uploadUrl' in multipart)
  );
}

export function createPersistedUploadStore(deps: {
  indexedDB?: IDBFactory;
  storage?: UploadBlobStorage;
  now?: () => number;
}): PersistedUploadStore {
  const { storage } = deps;
  const now = deps.now ?? Date.now;
  let database: Promise<IDBDatabase | null> | null = null;
  const openDatabase = () => {
    database ??= openUploadDatabase(deps.indexedDB).then((db) => {
      if (db)
        db.onversionchange = () => {
          db.close();
          database = null;
        };
      else database = null;
      return db;
    });
    return database;
  };
  let askedToPersist = false;
  const askToPersist = () => {
    if (askedToPersist) return;
    askedToPersist = true;
    // Best effort: a persisted origin is not evicted under storage pressure.
    void Promise.resolve()
      .then(() => storage?.persist?.())
      .catch(() => undefined);
  };

  const writeBytes = async (
    db: IDBDatabase,
    uploadId: string,
    file: Blob
  ): Promise<PersistedUpload['blobStore']> => {
    if (await writeOpfsBlob(storage, uploadId, file)) return 'opfs';
    try {
      await runUploadRequest(db, BLOBS_STORE, 'readwrite', (store) =>
        store.put(file, uploadId)
      );
      return 'idb';
    } catch {
      return 'none';
    }
  };

  const removeBytes = async (db: IDBDatabase | null, uploadId: string) => {
    await removeOpfsBlob(storage, uploadId);
    if (!db) return;
    await runUploadRequest(db, BLOBS_STORE, 'readwrite', (store) =>
      store.delete(uploadId)
    ).catch(() => undefined);
  };

  /** One tiny row and one tiny file, written and deleted again. */
  const probe = async (): Promise<boolean> => {
    const db = await openDatabase();
    if (!db) return false;
    try {
      await runUploadRequest(db, UPLOADS_STORE, 'readwrite', (store) =>
        store.put({ uploadId: PROBE_ID })
      );
      await runUploadRequest(db, UPLOADS_STORE, 'readwrite', (store) =>
        store.delete(PROBE_ID)
      );
    } catch {
      return false;
    }
    const kept = await writeBytes(db, PROBE_ID, new Blob([new Uint8Array(1)]));
    await removeBytes(db, PROBE_ID);
    return kept !== 'none';
  };

  return {
    async persistUpload(input, file) {
      const at = now();
      const fields = {
        blobStore: 'none',
        createdAt: at,
        updatedAt: at,
      } as const;
      // Picked field by field: nothing the caller adds reaches the disk.
      const picked = persistedUploadOf({ ...input, ...fields });
      const unsaved: PersistedUpload = picked ?? { ...input, ...fields };
      const db = picked ? await openDatabase() : null;
      // Without the metadata nothing could find the bytes again.
      if (!db) return unsaved;
      askToPersist();
      const record: PersistedUpload = {
        ...unsaved,
        blobStore: await writeBytes(db, input.uploadId, file),
      };
      try {
        await runUploadRequest(db, UPLOADS_STORE, 'readwrite', (store) =>
          store.put(record)
        );
        return record;
      } catch {
        await removeBytes(db, input.uploadId);
        return unsaved;
      }
    },

    async updateUpload(uploadId, patch) {
      const db = await openDatabase();
      if (!db) return null;
      return mutateUploadRow(db, uploadId, (current) => {
        const row = persistedUploadOf(current);
        return row
          ? persistedUploadOf(mergePersistedUpload(row, patch, now()))
          : null;
      }).catch(() => null);
    },

    async listUploads() {
      const db = await openDatabase();
      if (!db) return [];
      const rows = await runUploadRequest(
        db,
        UPLOADS_STORE,
        'readonly',
        (store) => store.getAll()
      ).catch((): unknown[] => []);
      const records: PersistedUpload[] = [];
      for (const row of rows) {
        const record = persistedUploadOf(row);
        if (!record) continue;
        records.push(record);
        // An old row's control URL is dropped here and overwritten on disk.
        if (carriesControlUrl(row))
          await runUploadRequest(db, UPLOADS_STORE, 'readwrite', (store) =>
            store.put(record)
          ).catch(() => undefined);
      }
      return records;
    },

    async readUploadFile(record) {
      let blob: Blob | null = null;
      if (record.blobStore === 'opfs') {
        blob = await readOpfsBlob(storage, record.uploadId);
      } else if (record.blobStore === 'idb') {
        const db = await openDatabase();
        const stored: unknown = db
          ? await runUploadRequest(db, BLOBS_STORE, 'readonly', (store) =>
              store.get(record.uploadId)
            ).catch(() => null)
          : null;
        blob = stored instanceof Blob ? stored : null;
      }
      // Bytes that no longer match the recorded size are not this file.
      if (!blob || blob.size !== record.sizeBytes) return null;
      return new File([blob], record.filename, {
        type: record.mimeType,
        lastModified: record.lastModified,
      });
    },

    async deleteUpload(uploadId) {
      try {
        const db = await openDatabase();
        await removeBytes(db, uploadId);
        if (!db) return;
        await runUploadRequest(db, UPLOADS_STORE, 'readwrite', (store) =>
          store.delete(uploadId)
        ).catch(() => undefined);
      } catch {
        // Deleting is cleanup: never fail the caller.
      }
    },

    probeDurableStorage: () => probe().catch(() => false),
  };
}

/**
 * This browser's real backends: IndexedDB and OPFS (`navigator.storage`),
 * read from the globals at call time. Empty during SSR or where a backend is
 * missing, so the store degrades instead of throwing.
 */
export function browserUploadStoreDeps(): {
  indexedDB?: IDBFactory;
  storage?: UploadBlobStorage;
} {
  // Plain global reads (not a `globalThis` alias, which the metered-provider
  // boundary treats as dynamic code).
  const idb = typeof indexedDB === 'undefined' ? undefined : indexedDB;
  const storage =
    typeof navigator === 'undefined'
      ? undefined
      : (navigator.storage as UploadBlobStorage | undefined);
  return {
    ...(idb ? { indexedDB: idb } : {}),
    // Called as `storage.getDirectory()`, so `this` stays the StorageManager.
    ...(storage ? { storage } : {}),
  };
}

let defaultStore: {
  store: PersistedUploadStore;
  indexedDB?: IDBFactory;
  storage?: UploadBlobStorage;
} | null = null;

/**
 * The store on this browser's IndexedDB and OPFS (inert during SSR). Built
 * once per pair of backends, so a store first made without them (SSR, a
 * test) is replaced once they exist.
 */
export function browserPersistedUploadStore(): PersistedUploadStore {
  const deps = browserUploadStoreDeps();
  if (
    !defaultStore ||
    defaultStore.indexedDB !== deps.indexedDB ||
    defaultStore.storage !== deps.storage
  )
    defaultStore = { ...deps, store: createPersistedUploadStore(deps) };
  return defaultStore.store;
}

const browserStore = browserPersistedUploadStore;

export const persistUpload: PersistedUploadStore['persistUpload'] = (
  record,
  file
) => browserStore().persistUpload(record, file);

export const updateUpload: PersistedUploadStore['updateUpload'] = (
  uploadId,
  patch
) => browserStore().updateUpload(uploadId, patch);

export const listUploads: PersistedUploadStore['listUploads'] = () =>
  browserStore().listUploads();

export const readUploadFile: PersistedUploadStore['readUploadFile'] = (
  record
) => browserStore().readUploadFile(record);

export const deleteUpload: PersistedUploadStore['deleteUpload'] = (uploadId) =>
  browserStore().deleteUpload(uploadId);
